/**
 * Coalesced publishing of server bulk frames (`${name}-bulk-updated`).
 *
 * A streamed deck arrives as one 100-row frame per WebSocket message. Before
 * this module every frame was ONE Zustand `setState` — one synchronous run of
 * every store subscriber (the whole Home tree included) per 100 cards, and one
 * clone of the outer map plus each touched bucket per frame (a 7k-card deck
 * cloned its bucket 70 times, 100 → 7000 rows). Frames now land in a
 * per-entity draft and are published in ONE `setState` per animation frame.
 *
 * Load-bearing rules (each one is a bug when broken):
 *
 * - THE PUBLISH RUNS INSIDE `withServerSource`. A deferred store write
 *   outside it reads as a local edit: the dirty scan would re-push the whole
 *   stream to the server.
 * - THE DRAFT HOLDS ENTRIES, NOT A SNAPSHOT. The outer map and each touched
 *   bucket are cloned from the CURRENT store at publish time, and a key the
 *   store changed since the frame was staged keeps the store's value — so a
 *   local write made while frames were pending wins, whether to another card
 *   of the deck or to the very card a frame carried. That is the order the
 *   per-frame apply had: the frame landed first, the later local write won.
 * - PERSISTENCE AND TIMESTAMPS STAY PER FRAME. `storage.put` and
 *   `serverTsRef` / `saveServerSyncedTs` run when the frame arrives; only the
 *   store publish is deferred. Disk may run ahead of memory, never behind.
 * - EVERY READER OF THE STORE THAT MUST SEE THE FRAMES DRAINS FIRST,
 *   synchronously: the `deck-cards-loaded` handler before it flips residency
 *   and wakes `awaitDeckRehydrated` callers; a single, delete, or bulk-delete
 *   broadcast for the same entity (a pending upsert must not resurrect a
 *   deleted row); `linked-cards-updated`; `handleSyncDiff` and the offline
 *   flush before they snapshot the store; socket loss and unbind; the native
 *   app-background flush. Handlers are dispatched un-serialised
 *   (`void Promise.resolve(adapter.onProtocolMessage(...))`), so nothing in
 *   the drain path may `await`.
 * - A HARD CAP. Once an entity's draft holds `BULK_COALESCE_MAX_ENTRIES` it
 *   publishes immediately, so a long stream costs O(rows / cap) publishes and
 *   bounded draft memory rather than one publish at the end.
 * - THE DEFAULT SCHEDULER IS THE NEXT ANIMATION FRAME, WITH A TIMER FALLBACK.
 *   rAF aligns the publish with paint (one render per frame of frames) but
 *   never fires in a hidden browser tab; the fallback timer bounds the
 *   deferral there. Both are cancelled by whichever fires first. Specs inject
 *   a synchronous scheduler (`setBulkFlushScheduler`).
 */

/** Publishes at most this many pending entries per entity before forcing a
 *  publish. ~20 frames of the 100-row stream. */
export const BULK_COALESCE_MAX_ENTRIES = 2000;

/** Upper bound on the deferral when `requestAnimationFrame` does not fire
 *  (hidden tab). Browsers clamp hidden-tab timers further; that is fine. */
export const BULK_FLUSH_FALLBACK_MS = 50;

/** Schedules one flush; returns a cancel. Injectable so specs run it inline. */
export type BulkFlushScheduler = (flush: () => void) => () => void;

const nextFrameOrTimer: BulkFlushScheduler = (flush) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cancel();
    flush();
  };
  const g = globalThis as {
    requestAnimationFrame?: (cb: () => void) => number;
    cancelAnimationFrame?: (id: number) => void;
  };
  const raf =
    typeof g.requestAnimationFrame === 'function' ? g.requestAnimationFrame(run) : null;
  const timer = setTimeout(run, raf === null ? 0 : BULK_FLUSH_FALLBACK_MS);
  const cancel = () => {
    clearTimeout(timer);
    if (raf !== null && typeof g.cancelAnimationFrame === 'function')
      g.cancelAnimationFrame(raf);
  };
  return () => {
    done = true;
    cancel();
  };
};

let scheduler: BulkFlushScheduler = nextFrameOrTimer;

/** Test seam — `null` restores the animation-frame default. */
export function setBulkFlushScheduler(next: BulkFlushScheduler | null): void {
  scheduler = next ?? nextFrameOrTimer;
}

/** What an entity registers while its draft is non-empty. */
export type PendingBulk = {
  /** Publish the draft to the store now (ONE `setState`, server source). */
  publish: () => void;
  /** Drop the draft without publishing (`reset()`: the store is being wiped). */
  discard: () => void;
};

/** One registration per entity `stateKey` with a non-empty draft. Insertion
 *  order is publish order, so `deckProperties` frames that preceded `cards`
 *  frames on the wire also land first. */
const pending = new Map<string, PendingBulk>();
let cancelScheduled: (() => void) | null = null;

function cancelScheduledFlush(): void {
  if (!cancelScheduled) return;
  cancelScheduled();
  cancelScheduled = null;
}

/**
 * Registers `stateKey`'s draft (idempotent while it stays pending) and makes
 * sure one flush is scheduled. Called by the entity when its draft goes from
 * empty to non-empty.
 */
export function schedulePendingBulk(stateKey: string, handlers: PendingBulk): void {
  pending.set(stateKey, handlers);
  if (cancelScheduled) return;
  // A scheduler may run the flush SYNCHRONOUSLY (the spec seam). The flush
  // nulls `cancelScheduled`; keeping the handle it returns would then leave a
  // stale "already scheduled" mark and starve every later frame — so the
  // handle is stored only when the flush has not run yet.
  cancelScheduled = NOT_YET_SCHEDULED;
  const cancel = scheduler(() => {
    cancelScheduled = null;
    drainPendingBulk();
  });
  if (cancelScheduled !== null) cancelScheduled = cancel;
}

const NOT_YET_SCHEDULED = () => {};

function takePending(stateKey?: string): PendingBulk[] {
  if (stateKey !== undefined) {
    const one = pending.get(stateKey);
    if (!one) return [];
    pending.delete(stateKey);
    if (pending.size === 0) cancelScheduledFlush();
    return [one];
  }
  cancelScheduledFlush();
  // Snapshot: a handler that throws must not strand the others, and a publish
  // may legitimately re-register (a frame handled during a subscriber run).
  const all = Array.from(pending.values());
  pending.clear();
  return all;
}

function runAll(handlers: PendingBulk[], method: keyof PendingBulk): void {
  let firstError: unknown = null;
  for (const h of handlers) {
    try {
      h[method]();
    } catch (err) {
      if (firstError === null) firstError = err;
    }
  }
  if (firstError !== null) throw firstError;
}

/**
 * Publishes pending drafts NOW, synchronously — one entity when `stateKey` is
 * given, every entity otherwise. Safe to call when nothing is pending. A drain
 * removes the registration; the entity re-registers on its next frame.
 */
export function drainPendingBulk(stateKey?: string): void {
  runAll(takePending(stateKey), 'publish');
}

/**
 * Drops every pending draft WITHOUT publishing. Only for `reset()` — the
 * previous user's frames must not land in the next user's store. Their rows
 * are already on disk (persistence is per frame).
 */
export function discardPendingBulk(): void {
  runAll(takePending(), 'discard');
}

/** Whether a publish is pending — for `stateKey`, or for any entity. */
export function hasPendingBulk(stateKey?: string): boolean {
  return stateKey === undefined ? pending.size > 0 : pending.has(stateKey);
}
